import React, { useMemo, type JSX } from 'react';
import query from './FakturabilderQuery.gql';
import {
  Fakturabilder as IData,
  FakturabilderVariables as IVariables,
  Fakturabilder_node_TEDLRLeverantorfaktura,
} from './_QueryTypes/Fakturabilder';
import { Query } from '@apollo/client/react/components';
import Logger from '../../../../../../../Utils/Logger';
import { FakturabildFormat } from '../../../../../Ekonomi/Rutiner/EkonomiskAnalys/Components/VerifikationModal/Queries/VerifikationQuery/VerifikationQuery';

interface IProps {
  children: (fakturaBilder: IFakturaBilder) => JSX.Element;
  id: string | null;
  leverantorfaktura?: Fakturabilder_node_TEDLRLeverantorfaktura;
}

export interface IFakturaBilder {
  xml: boolean;
  skannadSvefaktura: boolean;
  bilder: string[];
  format?: FakturabildFormat;
  bokfDatum?: string;
  journalNr?: string;
  loading?: boolean;
  defAnkVernr: string;
  bildUrls: string[];
}

export const reduceFakturasidor = (
  leverantorFaktura: Fakturabilder_node_TEDLRLeverantorfaktura
) => {
  return leverantorFaktura?._Associations?.Fakturasidor?.reduce<IFakturaBilder>(
    (a, b) => {
      if (b._Associations && b._Associations.Blob) {
        const { XML, BILD, BildUrler } = b._Associations.Blob;
        if (XML) {
          a.xml = true;
        }
        if (BILD) {
          a.bilder.push(BILD);
        }

        if (BildUrler && BildUrler !== '') {
          a.bildUrls = BildUrler?.split('|');
        }

        if (
          leverantorFaktura?.__typename === 'TEDLRLeverantorfaktura' &&
          leverantorFaktura.SkannadSvefaktura
        ) {
          a.skannadSvefaktura = true;
        }

        if (
          leverantorFaktura?.__typename === 'TEDLRLeverantorfaktura' &&
          leverantorFaktura.DefAnkVernr
        ) {
          a.defAnkVernr = leverantorFaktura.DefAnkVernr;
        }
      }
      if (
        leverantorFaktura?.__typename === 'TEDLRLeverantorfaktura' &&
        leverantorFaktura._Associations?.Fakturasidor?.[0]?.FORMAT
      ) {
        if (
          leverantorFaktura._Associations?.Fakturasidor?.[0]?.FORMAT === 'KOB'
        ) {
          a.format = 'KOB';
        } else if (
          leverantorFaktura._Associations?.Fakturasidor?.[0]?.FORMAT ===
          'SVEFAKTURA'
        ) {
          a.format = 'SVEFAKTURA';
        }
      }
      if (
        leverantorFaktura?.__typename === 'TEDLRLeverantorfaktura' &&
        leverantorFaktura?._Associations?.Registrering?.Bokfdatum
      ) {
        a.bokfDatum = leverantorFaktura._Associations.Registrering.Bokfdatum;
      }
      if (
        leverantorFaktura?.__typename === 'TEDLRLeverantorfaktura' &&
        leverantorFaktura?._Associations?.Registrering?._Associations
          ?.Bokfverifikationer?.Journalnr
      ) {
        a.journalNr =
          leverantorFaktura._Associations?.Registrering._Associations.Bokfverifikationer.Journalnr.toString();
      }
      return a;
    },
    {
      xml: false,
      bilder: [],
      skannadSvefaktura: false,
      defAnkVernr: '',
      bildUrls: [],
    }
  );
};

const FakturabilderQuery: React.FunctionComponent<IProps> = props => {
  const reducedFakturabild = useMemo(() => {
    if (
      props.leverantorfaktura &&
      props.leverantorfaktura.__typename === 'TEDLRLeverantorfaktura' &&
      props.leverantorfaktura._Associations &&
      props.leverantorfaktura._Associations.Fakturasidor
    ) {
      return reduceFakturasidor(props.leverantorfaktura);
    } else {
      return undefined;
    }
  }, [props.leverantorfaktura]);

  if (reducedFakturabild) {
    return props.children(reducedFakturabild);
  }

  const skip = props.id === null;

  return (
    <Query<IData, IVariables>
      query={query}
      skip={skip}
      variables={{ id: props.id || '' }}
    >
      {({ data, loading, error }) => {
        if (loading) {
          return props.children({
            xml: false,
            bilder: [],
            skannadSvefaktura: false,
            loading,
            defAnkVernr: '',
            bildUrls: [],
          });
        } else if (error) {
          Logger.log(
            'error',
            'FakturaBildError',
            error.message,
            error,
            undefined,
            { fingerprint: 'FakturaBildError', reporter: 'FakturabilderQuery' }
          );
          return null;
        }
        if (
          data &&
          data.node &&
          data.node.__typename === 'TEDLRLeverantorfaktura' &&
          data.node._Associations &&
          data.node._Associations.Fakturasidor
        ) {
          const reducedFakturabild = reduceFakturasidor(data.node);
          if (reducedFakturabild) {
            return props.children(reducedFakturabild);
          }
        }

        return props.children({
          xml: false,
          bilder: [],
          skannadSvefaktura: false,
          loading: skip,
          defAnkVernr: '',
          bildUrls: [],
        });
      }}
    </Query>
  );
};

export default FakturabilderQuery;
